import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { useCallback } from "react";

import { clientEnvModule } from "@/modules/client-env";
import { electronModule } from "@/modules/electron";
import { logger } from "@/modules/logger";
import { MDSIcon, MDSIconColor, MDSIconKind } from "@/mds/components/icon";
import { MDSLink, MDSTextVariant } from "@/mds/components/text";
import { trackAgentOverlayEntryClick } from "@/pages/agent/analytics/use-agent-overlay-analytics";
import {
  AGENT_INTENT_MODEL_V2_FEATURE_FLAG,
  resolveAgentOverlayEntryCopy,
} from "@/pages/agent/components/agent-overlay-entry.constants";
import { resolveAgentOverlayEntryMetrics } from "@/pages/agent/utils/agent-overlay-entry-metrics.utils";
import { useAppStore } from "@/store";

export const FloatingAgentConnectedWorkLink = observer(
  ({ onCollapse }: { onCollapse: () => void }) => {
    const { store } = useAppStore();
    const entryMetrics = resolveAgentOverlayEntryMetrics({
      tasks: store.assistantTasks.currentAssistantModels,
      projects: store.assistantProjects.currentAssistantModels,
      taskCountLoaded: store.assistantTasks.currentAssistantModelsLoaded,
      projectCountLoaded: store.assistantProjects.currentAssistantModelsLoaded,
    });
    const supportsGoals =
      store.spaceAccountFeatureFlags.getFlagStatus(AGENT_INTENT_MODEL_V2_FEATURE_FLAG) === true;
    const goalTrackingIsEnabled = supportsGoals && store.agentOverlay.isFeatureEnabled;
    const entryCopy = resolveAgentOverlayEntryCopy(goalTrackingIsEnabled);
    const openConnectedWork = useCallback(() => {
      trackAgentOverlayEntryClick({
        entryPoint: "floating_agent",
        isSummaryLoaded: entryMetrics.isLoaded,
        taskCount: entryMetrics.taskCount,
        projectCount: entryMetrics.projectCount,
      });

      onCollapse();

      if (clientEnvModule.isDesktop()) {
        void electronModule.floatingAgent.openTasks().catch((error: unknown) => {
          logger.warn({
            message:
              "[FloatingAgentConnectedWorkLink] Failed to open connected work in main window",
            info: { error: String(error) },
          });
        });
        return;
      }

      store.agentOverlay.openTasks();
    }, [entryMetrics, onCollapse, store.agentOverlay]);

    if (!store.agentOverlay.isTaskProjectTrackingEnabled) return null;

    return (
      <StyledConnectedWorkLink
        aria-label={entryCopy.openAccessibleLabel}
        variant={MDSTextVariant.TextXs}
        onPress={openConnectedWork}
      >
        <MDSIcon
          aria-hidden="true"
          kind={MDSIconKind.ChartNetwork}
          color={MDSIconColor.Inherit}
          size={12}
          boxSize={14}
        />
        <StyledConnectedWorkText>{entryCopy.label}</StyledConnectedWorkText>
      </StyledConnectedWorkLink>
    );
  }
);

const StyledConnectedWorkLink = styled(MDSLink)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["1"]};
  min-width: 0;
  overflow: hidden;
  color: ${({ theme }) => theme.colors.text.link.default};
  text-decoration: none;
  -webkit-app-region: no-drag;

  &:hover:not([data-disabled]) {
    text-decoration: underline;
  }
`;

const StyledConnectedWorkText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
